import React, { useState, useEffect, useCallback, useRef } from 'react';

interface PathNode {
  id: string;
  x: number;
  y: number;
  state: 'locked' | 'available' | 'answering' | 'correct' | 'incorrect' | 'complete' | 'remedial';
  label: string;
  isRemedial?: boolean;
  parentIds: string[];
}

interface AdaptivePathwayModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdaptivePathwayModal: React.FC<AdaptivePathwayModalProps> = ({ isOpen, onClose }) => {
  const [nodes, setNodes] = useState<PathNode[]>([]);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [simulationStep, setSimulationStep] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize the base path
  const initializePath = useCallback(() => {
    const baseNodes: PathNode[] = [
      { id: 'n1', x: 200, y: 50, state: 'complete', label: 'Intro', parentIds: [] },
      { id: 'n2', x: 140, y: 130, state: 'complete', label: 'Basics', parentIds: ['n1'] },
      { id: 'n3', x: 260, y: 130, state: 'complete', label: 'Concepts', parentIds: ['n1'] },
      { id: 'n4', x: 200, y: 210, state: 'available', label: 'Practice 1', parentIds: ['n2', 'n3'] },
      { id: 'n5', x: 120, y: 290, state: 'locked', label: 'Advanced A', parentIds: ['n4'] },
      { id: 'n6', x: 280, y: 290, state: 'locked', label: 'Advanced B', parentIds: ['n4'] },
      { id: 'n7', x: 200, y: 370, state: 'locked', label: 'Synthesis', parentIds: ['n5', 'n6'] },
      { id: 'n8', x: 200, y: 450, state: 'locked', label: 'Mastery', parentIds: ['n7'] },
    ];
    setNodes(baseNodes);
    setCurrentNodeId('n4');
    setSimulationStep(0);
  }, []);

  // Simulation scenarios
  const simulationScenarios = [
    { nodeId: 'n4', result: 'incorrect', description: 'Student struggles with Practice 1' },
    { nodeId: 'r1', result: 'correct', description: 'Remedial lesson helps!' },
    { nodeId: 'n4', result: 'correct', description: 'Retry Practice 1 - Success!' },
    { nodeId: 'n5', result: 'correct', description: 'Advanced A completed' },
    { nodeId: 'n6', result: 'incorrect', description: 'Struggles with Advanced B' },
    { nodeId: 'r2', result: 'correct', description: 'Extra practice added' },
    { nodeId: 'n6', result: 'correct', description: 'Advanced B mastered!' },
    { nodeId: 'n7', result: 'correct', description: 'Synthesis complete' },
    { nodeId: 'n8', result: 'correct', description: 'Mastery achieved!' },
  ];

  const runSimulationStep = useCallback(() => {
    if (simulationStep >= simulationScenarios.length) {
      // Reset after completion
      setTimeout(() => {
        initializePath();
        setIsAnimating(false);
      }, 2000);
      return;
    }

    const scenario = simulationScenarios[simulationStep];
    setIsAnimating(true);

    // Phase 1: Show answering state
    setNodes(prev => prev.map(n =>
      n.id === scenario.nodeId ? { ...n, state: 'answering' as const } : n
    ));
    setCurrentNodeId(scenario.nodeId);

    // Phase 2: Show result after delay
    setTimeout(() => {
      if (scenario.result === 'incorrect') {
        // Mark as incorrect and add remedial node
        setNodes(prev => {
          const currentNode = prev.find(n => n.id === scenario.nodeId);
          if (!currentNode) return prev;

          const remedialId = scenario.nodeId === 'n4' ? 'r1' : 'r2';
          const remedialExists = prev.some(n => n.id === remedialId);

          if (remedialExists) {
            return prev.map(n =>
              n.id === scenario.nodeId ? { ...n, state: 'incorrect' as const } : n
            );
          }

          // Add remedial node
          const remedialNode: PathNode = {
            id: remedialId,
            x: currentNode.x + (remedialId === 'r1' ? -80 : 80),
            y: currentNode.y + 40,
            state: 'available',
            label: remedialId === 'r1' ? 'Review' : 'Extra Help',
            isRemedial: true,
            parentIds: [scenario.nodeId],
          };

          return [
            ...prev.map(n =>
              n.id === scenario.nodeId ? { ...n, state: 'incorrect' as const } : n
            ),
            remedialNode,
          ];
        });
      } else {
        // Mark as correct and unlock next nodes
        setNodes(prev => {
          const updatedNodes = prev.map(n => {
            if (n.id === scenario.nodeId) {
              return { ...n, state: 'complete' as const };
            }
            // Unlock children if all parents are complete
            if (n.parentIds.includes(scenario.nodeId) && n.state === 'locked') {
              const allParentsComplete = n.parentIds.every(pid => {
                const parent = prev.find(p => p.id === pid);
                return parent?.state === 'complete' || pid === scenario.nodeId;
              });
              if (allParentsComplete) {
                return { ...n, state: 'available' as const };
              }
            }
            return n;
          });

          // If completing a remedial, make the original node available again
          if (scenario.nodeId.startsWith('r')) {
            const originalId = scenario.nodeId === 'r1' ? 'n4' : 'n6';
            return updatedNodes.map(n =>
              n.id === originalId ? { ...n, state: 'available' as const } : n
            );
          }

          return updatedNodes;
        });
      }

      setIsAnimating(false);
      setSimulationStep(prev => prev + 1);
    }, 1200);
  }, [simulationStep, simulationScenarios, initializePath]);

  // Auto-play simulation
  useEffect(() => {
    if (isOpen && isAutoPlaying && !isAnimating) {
      animationTimeoutRef.current = setTimeout(() => {
        runSimulationStep();
      }, 1500);
    }
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [isOpen, isAutoPlaying, isAnimating, runSimulationStep]);

  // Initialize on open
  useEffect(() => {
    if (isOpen) {
      initializePath();
      setIsAutoPlaying(true);
    }
  }, [isOpen, initializePath]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getNodeColor = (state: PathNode['state'], isRemedial?: boolean) => {
    if (isRemedial) return '#f59e0b';
    switch (state) {
      case 'complete': return '#10b981';
      case 'correct': return '#10b981';
      case 'available': return '#3b82f6';
      case 'answering': return '#8b5cf6';
      case 'incorrect': return '#ef4444';
      case 'remedial': return '#f59e0b';
      case 'locked': return '#475569';
      default: return '#475569';
    }
  };

  const getNodeOpacity = (state: PathNode['state']) => {
    return state === 'locked' ? 0.4 : 1;
  };

  // Hexagon path
  const hexPath = (cx: number, cy: number, size: number) => {
    const points = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${cx + size * Math.cos(angle)},${cy + size * Math.sin(angle)}`);
    }
    return `M ${points.join(' L ')} Z`;
  };

  const currentScenario = simulationScenarios[simulationStep - 1];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
        >
          <div>
            <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>
              Adaptive Learning Pathway
            </h2>
            <p className="text-sm mt-1" style={{ color: '#94a3b8' }}>
              Watch how the path adapts based on student performance
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: isAutoPlaying ? 'rgba(245, 158, 11, 0.2)' : 'rgba(59, 130, 246, 0.2)',
                color: isAutoPlaying ? '#f59e0b' : '#3b82f6',
                border: `1px solid ${isAutoPlaying ? 'rgba(245, 158, 11, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`,
              }}
            >
              {isAutoPlaying ? '⏸ Pause' : '▶ Play'}
            </button>
            <button
              onClick={() => {
                initializePath();
                setIsAutoPlaying(true);
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#94a3b8',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              ↻ Reset
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(255, 255, 255, 0.05)' }}
            >
              <svg className="w-5 h-5" style={{ color: '#94a3b8' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="flex" style={{ height: 'calc(90vh - 140px)' }}>
          {/* Path visualization */}
          <div className="flex-1 p-6 overflow-auto">
            <svg width="400" height="520" className="mx-auto block">
              <defs>
                <filter id="node-glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="pulse-glow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Connection lines */}
              {nodes.map(node =>
                node.parentIds.map(parentId => {
                  const parent = nodes.find(n => n.id === parentId);
                  if (!parent) return null;

                  const isActive = node.state !== 'locked' || parent.state === 'complete';
                  const isRemedialConnection = node.isRemedial || parent.isRemedial;

                  return (
                    <line
                      key={`${parentId}-${node.id}`}
                      x1={parent.x}
                      y1={parent.y}
                      x2={node.x}
                      y2={node.y}
                      stroke={isRemedialConnection ? '#f59e0b' : isActive ? '#3b82f6' : '#334155'}
                      strokeWidth={isRemedialConnection ? 2 : 3}
                      strokeDasharray={isRemedialConnection ? '4 4' : 'none'}
                      opacity={isActive ? 0.6 : 0.3}
                      style={{ transition: 'all 0.5s ease' }}
                    />
                  );
                })
              )}

              {/* Nodes */}
              {nodes.map(node => {
                const color = getNodeColor(node.state, node.isRemedial);
                const opacity = getNodeOpacity(node.state);
                const isCurrentNode = currentNodeId === node.id;
                const size = node.isRemedial ? 20 : 28;

                return (
                  <g key={node.id} style={{ transition: 'all 0.5s ease' }}>
                    {/* Shadow */}
                    <path
                      d={hexPath(node.x, node.y + 4, size)}
                      fill={color}
                      opacity={0.2}
                    />

                    {/* Pulse ring for current node */}
                    {isCurrentNode && node.state === 'answering' && (
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={size + 10}
                        fill="none"
                        stroke={color}
                        strokeWidth="2"
                        opacity="0.5"
                        className="animate-ping"
                      />
                    )}

                    {/* Main hexagon */}
                    <path
                      d={hexPath(node.x, node.y, size)}
                      fill={color}
                      opacity={opacity}
                      filter={isCurrentNode ? 'url(#pulse-glow)' : 'url(#node-glow)'}
                      style={{
                        transition: 'all 0.3s ease',
                        transform: isCurrentNode ? 'scale(1.1)' : 'scale(1)',
                        transformOrigin: `${node.x}px ${node.y}px`,
                      }}
                    />

                    {/* Icon */}
                    {node.state === 'complete' && (
                      <text
                        x={node.x}
                        y={node.y + 4}
                        textAnchor="middle"
                        fill="white"
                        fontSize={node.isRemedial ? 10 : 14}
                        fontWeight="bold"
                      >
                        ✓
                      </text>
                    )}
                    {node.state === 'incorrect' && (
                      <text
                        x={node.x}
                        y={node.y + 4}
                        textAnchor="middle"
                        fill="white"
                        fontSize={14}
                        fontWeight="bold"
                      >
                        ✗
                      </text>
                    )}
                    {node.state === 'answering' && (
                      <text
                        x={node.x}
                        y={node.y + 4}
                        textAnchor="middle"
                        fill="white"
                        fontSize={12}
                      >
                        ...
                      </text>
                    )}
                    {(node.state === 'available' || node.state === 'locked') && (
                      <circle
                        cx={node.x}
                        cy={node.y}
                        r={node.isRemedial ? 4 : 6}
                        fill="white"
                        opacity={node.state === 'available' ? 0.9 : 0.3}
                      />
                    )}

                    {/* Label */}
                    <text
                      x={node.x}
                      y={node.y + size + 16}
                      textAnchor="middle"
                      fill={opacity === 1 ? '#e2e8f0' : '#64748b'}
                      fontSize="11"
                      fontWeight="500"
                      style={{ transition: 'fill 0.3s ease' }}
                    >
                      {node.label}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Info panel */}
          <div
            className="w-80 p-6 border-l overflow-auto"
            style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
          >
            {/* Current action */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#94a3b8' }}>
                Current Action
              </h3>
              <div
                className="p-4 rounded-lg"
                style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                }}
              >
                <p className="text-sm" style={{ color: '#e2e8f0' }}>
                  {currentScenario?.description || 'Starting simulation...'}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ background: '#8b5cf6' }}
                  />
                  <span className="text-xs" style={{ color: '#8b5cf6' }}>
                    Step {simulationStep} of {simulationScenarios.length}
                  </span>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#94a3b8' }}>
                How It Works
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(16, 185, 129, 0.2)' }}
                  >
                    <span style={{ color: '#10b981' }}>✓</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#e2e8f0' }}>
                      Correct Answer
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                      Progress to next lesson in the path
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(239, 68, 68, 0.2)' }}
                  >
                    <span style={{ color: '#ef4444' }}>✗</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#e2e8f0' }}>
                      Incorrect Answer
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                      Remedial content is added to reinforce concepts
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(245, 158, 11, 0.2)' }}
                  >
                    <span style={{ color: '#f59e0b' }}>↻</span>
                  </div>
                  <div>
                    <p className="text-xs font-medium" style={{ color: '#e2e8f0' }}>
                      Remedial Lesson
                    </p>
                    <p className="text-xs mt-1" style={{ color: '#64748b' }}>
                      Extra practice before retrying
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#94a3b8' }}>
                Node States
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Complete', color: '#10b981' },
                  { label: 'Available', color: '#3b82f6' },
                  { label: 'Answering', color: '#8b5cf6' },
                  { label: 'Incorrect', color: '#ef4444' },
                  { label: 'Remedial', color: '#f59e0b' },
                  { label: 'Locked', color: '#475569' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ background: item.color, opacity: item.label === 'Locked' ? 0.4 : 1 }}
                    />
                    <span className="text-xs" style={{ color: '#94a3b8' }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="mt-6 pt-6" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <h3 className="text-sm font-semibold mb-3" style={{ color: '#94a3b8' }}>
                Pathway Stats
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div
                  className="p-3 rounded-lg text-center"
                  style={{ background: 'rgba(16, 185, 129, 0.1)' }}
                >
                  <div className="text-lg font-bold" style={{ color: '#10b981' }}>
                    {nodes.filter(n => n.state === 'complete').length}
                  </div>
                  <div className="text-[10px]" style={{ color: '#64748b' }}>
                    Completed
                  </div>
                </div>
                <div
                  className="p-3 rounded-lg text-center"
                  style={{ background: 'rgba(245, 158, 11, 0.1)' }}
                >
                  <div className="text-lg font-bold" style={{ color: '#f59e0b' }}>
                    {nodes.filter(n => n.isRemedial).length}
                  </div>
                  <div className="text-[10px]" style={{ color: '#64748b' }}>
                    Remedial Added
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4" style={{ color: '#f59e0b' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-sm" style={{ color: '#94a3b8' }}>
              AI continuously adapts the learning path based on performance
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-32 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${(simulationStep / simulationScenarios.length) * 100}%`,
                  background: 'linear-gradient(90deg, #10b981, #0ea5e9)',
                }}
              />
            </div>
            <span className="text-xs" style={{ color: '#64748b' }}>
              {Math.round((simulationStep / simulationScenarios.length) * 100)}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdaptivePathwayModal;
